'use client';

import { Box, BoxProps, Center, Spinner, Stack, Text } from '@chakra-ui/react';
import { useIntersectionObserver } from '~/hooks/useIntersectionObserver';
import { ActionButton } from '../buttons/components/ActionButton';
import { HiArrowDown } from 'react-icons/hi';
import { useRef, useEffect, useState } from 'react';

// Give the infinite loader a break in between triggers
// to allow the HTML enough time to shift the layout
// Otherwise it keeps triggering instantly and breaks the user experience
// 2s felt good through experimentation, enough time for the user
// to do something and not let the page get stuck on a loading loop
const COOLDOWN_MS = 2_000;

interface InfiniteScrollTriggerProps {
  onVisible: () => void;
  isLoading: boolean;
  hasMore: boolean;
  infiniteScrollEnabled?: boolean;
  showCount?: boolean;
  results?: number;
  total?: number;
  buttonText?: string;
  showLoader?: boolean;
  boxProps?: BoxProps;
}

export const InfiniteScrollTrigger = ({
  onVisible,
  isLoading,
  hasMore,
  infiniteScrollEnabled = true,
  showCount = false,
  results = 0,
  total = 0,
  buttonText = 'Ladda fler',
  showLoader = false,
  boxProps,
}: InfiniteScrollTriggerProps) => {
  const [onCooldown, setOnCooldown] = useState<boolean>(infiniteScrollEnabled);
  const wasManuallyTriggered = useRef(false);

  const { setLastElement } = useIntersectionObserver({
    onIntersect: () => {
      if (wasManuallyTriggered.current) {
        wasManuallyTriggered.current = false;
        return;
      }
      onVisible();
      setOnCooldown(true);
    },
    isDisabled: !infiniteScrollEnabled || !hasMore || isLoading || onCooldown,
    rootMargin: '200px',
    threshold: 0,
  });

  useEffect(() => {
    if (!isLoading) {
      wasManuallyTriggered.current = false;
    }
  }, [isLoading]);

  const handleManualLoad = () => {
    wasManuallyTriggered.current = true;
    onVisible();
  };

  useEffect(() => {
    if (!infiniteScrollEnabled || !onCooldown) return;
    setTimeout(() => {
      setOnCooldown(false);
    }, COOLDOWN_MS);
  }, [onCooldown])

  const showLoadMoreButton = hasMore && results < total;

  return (
    <Box
      ref={infiniteScrollEnabled ? setLastElement : undefined}
      width="100%"
      {...boxProps}
    >
      <Center>
        <Stack spacing={3}>
          {/* Count */}
          {showCount && (
            <Text fontSize="sm" color="muted">
              {results} av {total} träffar
            </Text>
          )}

          {/* Button with spinner */}
          {showLoadMoreButton && (
            <ActionButton
              boxShadow={0}
              onClick={handleManualLoad}
              isDisabled={isLoading || onCooldown}
              leftIcon={(isLoading || onCooldown) ? <Spinner size="sm" /> : <HiArrowDown />}
              variant="subtle"
              hidden={!showLoader && isLoading}
            >
              {(isLoading || onCooldown) ? 'Laddar' : buttonText}
            </ActionButton>
          )}
        </Stack>
      </Center>
    </Box>
  );
};
